import React, { useEffect, useMemo, useState } from 'react';
import { Users2, Info, List, Map as MapIcon } from 'lucide-react';
import PageLayout from '../components/layout/PageLayout';
import FilterBar from '../components/features/FilterBar';
import CellCard from '../components/features/CellCard';
import MapView from '../components/features/MapView';
import { CellCardSkeleton } from '../components/ui/Skeleton';
import { api } from '../services/api';
import { church } from '../mock/data';
import { distanceKm } from '../lib/utils';
import { toast } from 'sonner';

const NAV_LINKS = [
  { label: 'Sobre Nós', to: '/sobre' },
  { label: 'Pregações', to: '/pregações' },
  { label: 'Células', to: '/celulas' },
  { label: 'Eventos', to: '/evento' },

  { label: 'Galeria', to: '/galeria' },
  { label: 'Contato', to: '/contato' },
];

const FOOTER_COLUMNS = [
  {
    title: 'Navegação',
    links: [
      ['Em que Cremos', '/sobre'],
      ['História', '/sobre'],
      ['Eventos', '/evento'],

      ['Células', '/celulas'],
      ['Galeria', '/galeria'],
    ],
  },
  {
    title: 'Institucional',
    links: [
      ['Política de Privacidade', '/privacidade'],
      ['Relatórios', '/relatorios'],
      ['Doações', '/contato'],
      ['Área Administrativa', '/admin'],
    ],
  },
];


export default function Locator() {
  const [cells, setCells] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useState('');
  const [activeNetwork, setActiveNetwork] = useState(null);
  const [activeNeighborhood, setActiveNeighborhood] = useState(null);
  const [origin, setOrigin] = useState(null);
  const [proximity, setProximity] = useState(false);
  const [locating, setLocating] = useState(false);
  const [view, setView] = useState('list');
  const [radiusKm, setRadiusKm] = useState(5);

  useEffect(() => {
    Promise.all([api.getCells(), api.getNetworks()]).then(([c, n]) => {
      setCells(c);
      setNetworks(n);
      setLoading(false);
    });
  }, []);

  const netById = useMemo(
    () => Object.fromEntries(networks.map((n) => [n.id, n])),
    [networks]
  );

  const neighborhoods = useMemo(
    () => [...new Set(cells.map((c) => c.address.neighborhood))].sort(),
    [cells]
  );

  function handleProximity() {
    if (proximity) {
      setProximity(false);
      setOrigin(null);
      return;
    }
    if (!navigator.geolocation) {
      toast.error('Geolocalização não suportada neste dispositivo.');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setOrigin({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setProximity(true);
        setLocating(false);
        toast.success('Ordenado pelas células mais próximas de você.');
      },
      () => {
        // Fallback to church center so the demo still works without permission.
        setOrigin(church.center);
        setProximity(true);
        setLocating(false);
        toast('Usando o centro de Caratinga como referência.');
      },
      { timeout: 8000 }
    );
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = cells.filter((c) => {
      const net = netById[c.networkId];
      if (activeNetwork && c.networkId !== activeNetwork) return false;
      if (activeNeighborhood && c.address.neighborhood !== activeNeighborhood) return false;
      if (q) {
        const hay = [
          c.name,
          c.leader.name,
          c.host.name,
          net?.supervisor?.name,
          c.address.neighborhood,
          c.address.city,
          net?.name,
        ]
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    list = list.map((c) => ({
      cell: c,
      network: netById[c.networkId],
      distance: origin ? distanceKm(origin, c.address) : null,
    }));

    if (proximity && origin) {
      list = list.filter((x) => x.distance != null && x.distance <= radiusKm);
      list.sort((a, b) => (a.distance ?? 1e9) - (b.distance ?? 1e9));
    }
    return list;
  }, [cells, netById, query, activeNetwork, activeNeighborhood, proximity, origin, radiusKm]);

  return (
    <PageLayout navLinks={NAV_LINKS} footerColumns={FOOTER_COLUMNS}>
      <div className="border-b border-slate-200/60 bg-canvas py-3">
        <div className="app-container">
          <div className="py-2">
            <h1 className="font-heading text-2xl font-extrabold tracking-tight text-ink md:text-3xl">
              Encontre uma Célula
            </h1>
            <p className="mt-0.5 text-sm text-ink-soft">
              {cells.length} células em {church.city} · {church.state}
            </p>
          </div>
        </div>
      </div>

      <div className="z-20 border-b border-slate-200/60 bg-canvas py-3">

        <div className="app-container">
          <FilterBar
            query={query}
            setQuery={setQuery}
            networks={networks}
            activeNetwork={activeNetwork}
            setActiveNetwork={setActiveNetwork}
            neighborhoods={neighborhoods}
            activeNeighborhood={activeNeighborhood}
            setActiveNeighborhood={setActiveNeighborhood}
            proximity={proximity}
            onProximity={handleProximity}
            locating={locating}
          />
        </div>
      </div>

      <div className="app-container py-4">
        {proximity && (
          <div className="no-scrollbar mb-3 flex items-center gap-2 overflow-x-auto" data-testid="radius-selector">
            <span className="shrink-0 text-xs font-semibold uppercase tracking-wider text-ink-faint">Raio</span>
            {[1, 3, 5, 10].map((r) => (
              <button
                key={r}
                onClick={() => setRadiusKm(r)}
                data-testid={`radius-${r}`}
                className={`shrink-0 rounded-full px-3 py-1.5 text-sm font-semibold font-heading transition-colors duration-200 ${radiusKm === r ? 'bg-primary text-white' : 'border border-slate-200 bg-white text-ink-soft'}`}
              >
                {r} km
              </button>
            ))}
          </div>
        )}

        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-ink-soft">
            <Users2 size={16} className="text-primary" />
            {loading ? 'Carregando...' : `${filtered.length} ${filtered.length === 1 ? 'célula' : 'células'}`}
          </div>
          <div className="flex gap-1 rounded-full border border-slate-200 bg-white p-1" data-testid="view-toggle">
            <button
              onClick={() => setView('list')}
              data-testid="view-list"
              className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors duration-200 ${view === 'list' ? 'bg-primary text-white' : 'text-ink-faint'}`}
              aria-label="Ver em lista"
            >
              <List size={16} />
            </button>
            <button
              onClick={() => setView('map')}
              data-testid="view-map"
              className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors duration-200 ${view === 'map' ? 'bg-primary text-white' : 'text-ink-faint'}`}
              aria-label="Ver no mapa"
            >
              <MapIcon size={16} />
            </button>
          </div>
        </div>

        {view === 'map' && !loading ? (
          <MapView items={filtered} origin={proximity ? origin : null} radiusKm={proximity ? radiusKm : null} />
        ) : (
          <div className="grid gap-3.5 md:grid-cols-2 xl:grid-cols-3">
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => <CellCardSkeleton key={i} />)
            ) : filtered.length === 0 ? (
              <div className="col-span-full rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center" data-testid="cells-empty">
                <Info size={28} className="mx-auto text-ink-faint" />
                <p className="mt-2 font-heading font-semibold text-ink">Nenhuma célula encontrada</p>
                <p className="mt-1 text-sm text-ink-soft">Tente ajustar os filtros ou a busca.</p>
              </div>
            ) : (
              filtered.map(({ cell, network, distance }, i) => (
                <CellCard key={cell.id} cell={cell} network={network} distance={distance} index={i} />
              ))
            )}
          </div>
        )}
      </div>
    </PageLayout>
  );
}


